/****************************************************
 * Module Name:  dayWiseMixStoneSales.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useEffect } from "react";
import { Box, Tooltip, Typography, TextField, Button, AppBar, Tabs, Tab, Stack } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Constants from "../../utility/constants";
import Utility from "../../utility/utility";
import CommonSnackbar from "../common/commonSnackBar";
import NoPermission from "../common/noPermission";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { LoadingButton } from "@mui/lab";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import renderCellExpand from "../common/dataGridCellExpand";
import { Modal } from "react-bootstrap";
import logo from '../../images/pansuriya.jpg';
import MixSaleProxy from "../../utility/proxy/manager/mixProxy/mixSalesProxy";
import MixSellInvoice from "./mixSellInvoice";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import SwipeableViews from "react-swipeable-views";
import CancelledMixBucketReport from "./cancelledMixBucketReport";
import PageTitle from "../common/pageTitle";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 1 }}>
                    <Typography component={"span"}>{children}</Typography>
                </Box>
            )}
        </Box>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `full-width-tab-${index}`,
        "aria-controls": `full-width-tabpanel-${index}`,
        value: index,
    };
}

const DayWiseMixStoneSales = () => {

    const theme = useTheme();
    const [value, setValue] = React.useState(0);
    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleChangeIndex = (index) => {
        setValue(index);
    };

    const dayWiseMixStoneSalesPermission = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.MIX_REPORT_DAY_WISE_STONE_SALES);

    const [stockData, setStockData] = useState([]);
    const [loader, setLoader] = useState();
    const [fromDate, setFromDate] = useState(dayjs().startOf("month"));
    const [toDate, setToDate] = useState(dayjs());
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const defaultStatus = [{ value: -1, label: "-- ANY --" }];
    const [invoiceDate, setInvoiceDate] = useState();

    const [totalSell, setTotalSell] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [totalCancelledStone, setTotalCancelledStone] = useState(0);
    const [totalCancelledAmount, setTotalCancelledAmount] = useState(0);
    const [effectiveAmount, setEffectiveAmount] = useState(0);

    const columns = [
        {
            headerName: "DATE",
            field: "date",
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            width: 180,
            renderCell: (params) => dayjs(params.value).format("DD/MM/YYYY"),
            headerAlign: "center",
            align: "center",
        },
        {
            headerName: "DISTINCT CUSTOMER",
            field: "distinctcustomer",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 290,
            renderCell: renderCellExpand
        },
        {
            headerName: "SELL COUNT",
            field: "sellcount",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 260,
            renderCell: (params) => {
                return (
                    <Button
                        sx={{
                            textDecorationLine: "underline",
                            color: 'black'
                        }}
                        onClick={() => {
                            setInvoiceDate(params.row.date)
                            setShowInvoiceModal(true)
                            setValue(0);
                        }}
                    >
                        {params.value}
                    </Button>
                )
            }
        },
        {
            headerName: "TOTAL AMOUNT",
            field: "totalamount",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 250,
            renderCell: (params) => (params.value).toFixed(2)
        },
        {
            headerName: "CANCELLED SELL",
            field: "cancelledstonecount",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 280,
            renderCell: (params) => {
                return (
                    <Button
                        sx={{
                            textDecorationLine: "underline",
                            color: 'black'
                        }}
                        onClick={() => {
                            setInvoiceDate(params.row.date)
                            setShowInvoiceModal(true)
                            setValue(1);
                        }}
                    >
                        {params.value}
                    </Button>
                )
            }
        },
        {
            headerName: "CANCELLED AMOUNT",
            field: "cancelledstoneamount",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 280,
            renderCell: (params) => (params.value).toFixed(2)
        },
        {
            headerName: "EFFECTIVE SELL",
            field: "effectiveamount",
            headerAlign: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            align: "center",
            width: 280,
            renderCell: (params) => (params.row.totalamount - params.row.cancelledstoneamount).toFixed(2)
        },
    ];

    useEffect(() => {
        PageTitle("MIX_REPORT_DAY_WISE_STONE_SALES");
        if (Utility.isReadAllowed(dayWiseMixStoneSalesPermission)) {
            handleSearch();
        }
    }, []);

    const handleSearch = () => {
        let data = {
            from: dayjs(fromDate).format('YYYY-MM-DD') + "T00:00:00.000Z",
            to: dayjs(toDate).format('YYYY-MM-DD') + "T23:59:59.999Z"
        }

        setLoader(true);
        MixSaleProxy.GetDayWiseMixBucketSales(data, (response) => {
            if (response) {
                let tempResponse = response.sort((a, b) => a.date.localeCompare(b.date));
                setStockData(tempResponse);
                getSummaryReport(tempResponse);
                setLoader(false);
            } else {
                setLoader(false);
                setShowSnackbar(true);
                setSnackbarMessage(Constants.APiFailErrorMessage);
                setSnackbarVariant("error");
            }
        });
    }

    const getSummaryReport = (response) => {
        let totalSell = 0, totalAmount = 0, totalCancelledStone = 0,
            totalCancelledAmount = 0, effectiveAmount = 0;
        response.forEach(iData => {
            totalSell += iData.sellcount;
            totalAmount += iData.totalamount;
            totalCancelledStone += iData.cancelledstonecount;
            totalCancelledAmount += iData.cancelledstoneamount;
            effectiveAmount += (iData.totalamount - iData.cancelledstoneamount);
        });

        setTotalSell(totalSell);
        setTotalAmount(totalAmount);
        setTotalCancelledStone(totalCancelledStone);
        setTotalCancelledAmount(totalCancelledAmount);
        setEffectiveAmount(effectiveAmount);
    }

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: { md: "none", lg: "block" } }} />
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexGrow: 1,
                        minWidth: 0,
                        px: 1,
                    }}
                >
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{
                            width: "100%",
                            alignItems: { xs: "flex-start", sm: "center" },
                            justifyContent: { xs: "flex-start", sm: "flex-start" },
                        }}
                    >
                        <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                            Total Sell: <b>{totalSell}</b>
                        </Typography>

                        <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                            Amount: <b>{totalAmount.toFixed(2)}</b>
                        </Typography>
                        <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                            Cancelled Stone: <b>{totalCancelledStone}</b>
                        </Typography>

                        <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                            Cancelled Amount:<b>{totalCancelledAmount.toFixed(2)}</b>
                        </Typography>
                        <Typography component="span" sx={{ fontSize: Constants.defaultTableFontSize }}>
                            Effective Sell: <b>{effectiveAmount.toFixed(2)}</b>
                        </Typography>
                    </Stack>
                </Box>

                {loader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            onClick={() => handleSearch()}
                            color="primary"
                            fontSize="medium"
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        );
    }

    return (
        <Box>
            {!Utility.isReadAllowed(dayWiseMixStoneSalesPermission) ? <NoPermission /> : (
                <Box sx={{ mt: "8px" }}>

                    <CommonSnackbar
                        show={showSnackbar}
                        message={snackbarMessage}
                        variant={snackbarVariant}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={snackbarVariant === "success" ? "Success" : snackbarVariant === "error" ? "Error" : "Warning"}
                        handleClose={() => setShowSnackbar(false)}
                    />

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            width: "100%",
                            mt: 1,
                            mb: 2,
                            gap: "15px",
                        }}
                    >

                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography sx={{ whiteSpace: "nowrap", mr: 1 }}>From</Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    ampm={true}
                                    renderInput={(props) => (
                                        <TextField
                                            {...props}
                                            size="small"
                                            sx={{ width: "200px" }}
                                            inputProps={{ ...props.inputProps, readOnly: true }}
                                        />
                                    )}
                                    value={fromDate}
                                    inputFormat="DD/MM/YYYY"
                                    onChange={(newValue) => {
                                        // Get the current time
                                        const now = dayjs();

                                        // Set startOfMonth to the first day of the selected month, but with the current system time
                                        const fromDate = dayjs(newValue)
                                            .hour(now.hour())
                                            .minute(now.minute())
                                            .second(now.second())
                                            .millisecond(now.millisecond())
                                            .toDate();

                                        setFromDate(fromDate);
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography sx={{ whiteSpace: "nowrap", mr: 1 }}>To</Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    className="picker"
                                    ampm={true}
                                    renderInput={(props) => (
                                        <TextField
                                            {...props}
                                            size="small"
                                            sx={{ width: "200px" }}
                                            inputProps={{ ...props.inputProps, readOnly: true }}
                                        />
                                    )}
                                    error={false}
                                    value={toDate}
                                    inputFormat="DD/MM/YYYY"
                                    onChange={(newValue) => {
                                        // Get the current time
                                        const now = dayjs();

                                        // Set startOfMonth to the first day of the selected month, but with the current system time
                                        const toDate = dayjs(newValue)
                                            .hour(now.hour())
                                            .minute(now.minute())
                                            .second(now.second())
                                            .millisecond(now.millisecond())
                                            .toDate();

                                        setToDate(toDate);
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>
                        <LoadingButton
                            variant="contained"
                            onClick={() => handleSearch()}
                            type="Submit"
                            loading={loader}
                            loadingPosition="start"
                            startIcon={<SearchOutlinedIcon />}
                        >
                            Search
                        </LoadingButton>
                    </Box>




                    {/* Table */}
                    <Box
                        sx={{
                            marginTop: "8px",
                            backgroundColor: '#fff',
                            color: '#000',
                            width: "100%",
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: '78vh'
                        }}
                    >
                        <DataGridPro
                            disableRowSelectionOnClick={true}
                            localeText={{ noRowsLabel: "No Record Found" }}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "4px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "12px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "18px",
                                },

                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                            density="compact"
                            slots={{ toolbar: CustomToolbar }}
                            rows={stockData}
                            columns={columns}
                            getRowId={(row) => row.date}
                            loading={loader}
                            onRowDoubleClick={(params) => {
                                setInvoiceDate(params.row.date)
                                setShowInvoiceModal(true)
                                setValue(0);
                            }}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns id, the other columns will remain visible
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: {
                                        debounceMs: 500,
                                    },
                                },
                            }}
                        />
                    </Box>

                    {showInvoiceModal ? (
                        <Modal
                            enforceFocus={false}
                            show={true}
                            onHide={() => setShowInvoiceModal(false)}
                            backdrop="static"
                            dialogClassName="max-modalSize-Tab"
                        >
                            <Modal.Header closeButton>
                                <Modal.Title>
                                    <img
                                        src={logo}
                                        height="40pt"
                                        width="40pt"
                                        alt={Constants.COMPANY_SITE}
                                    />
                                    &nbsp;&nbsp;
                                    Mix Invoice Details
                                </Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                {value === 2 ? <NoPermission /> :
                                    <Box sx={{
                                        bgcolor: "background.paper",
                                        width: "100%",
                                        padding: "10px",
                                        position: "relative"
                                    }}>
                                        <AppBar position="static" sx={{ bgcolor: "#03a9f4", height: 50 }}>
                                            <Tabs
                                                value={value}
                                                onChange={handleChange}
                                                indicatorColor="secondary"
                                                textColor="inherit"
                                                variant="fullWidth"
                                                aria-label="full width tabs example"
                                                sx={{
                                                    "& .MuiButtonBase-root .MuiTab-root": {
                                                        color: "#212121",
                                                        fontWeight: "bold",
                                                        textAlign: 'center',
                                                        minHeight: "50px",
                                                    },
                                                    "& .MuiTab-root": {
                                                        paddingBottom: "12px"
                                                    },
                                                    "& .Mui-selected": {
                                                        bgcolor: "#0277bd !important",
                                                        color: "#fafafa !important",
                                                        fontWeight: "bold !important",
                                                        minHeight: "50px",
                                                    }
                                                }}
                                                TabIndicatorProps={{ style: { background: "#0277bd" } }}
                                            >
                                                {/* Mix Invoice Details */}
                                                <Tab
                                                    iconPosition="start"
                                                    label="Mix Invoice Details"
                                                    {...a11yProps(0)}
                                                />

                                                {/* Cancelled Mix Stones */}
                                                <Tab
                                                    iconPosition="start"
                                                    label="Cancelled Mix Bucket"
                                                    {...a11yProps(1)}
                                                />
                                            </Tabs>
                                        </AppBar>
                                        <SwipeableViews
                                            axis={theme.direction === "rtl" ? "x-reverse" : "x"}
                                            index={value}
                                            onChangeIndex={handleChangeIndex}
                                        >
                                            {/* Mix Invoice Details */}
                                            <TabPanel value={value} index={0}>
                                                <MixSellInvoice
                                                    defaultfromdate={invoiceDate}
                                                    defaulttodate={invoiceDate}
                                                    defaultStatus={defaultStatus}
                                                    fromOtherComponent={true}
                                                    heightFromOtherComponent={"66vh"}
                                                />
                                            </TabPanel>

                                            {/* Cancelled Mix Stones */}
                                            <TabPanel value={value} index={1}>
                                                <CancelledMixBucketReport invoiceDate={invoiceDate} />
                                            </TabPanel>
                                        </SwipeableViews>

                                    </Box>}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="contained" color="error" onClick={() => setShowInvoiceModal(false)}>Close</Button>
                            </Modal.Footer>
                        </Modal>
                    ) : null}

                </Box>
            )}
        </Box>
    );
};

export default DayWiseMixStoneSales;